import React, {useCallback, useRef} from 'react';
import {Layout, LayoutScrollable} from '@di-nxt/components';
import {CellSpanInfo, SheetProps} from './types';
import {Row} from './Row';
import {getFilteredCellSpanInfos} from './functions';
import assert from 'assert-ts';
import {CellMemoizer} from './CellMemoizer';
import {CellSpanMemoizer} from './CellSpanMemoizer';

export const Sheet = function <TCellData = any, TCellSpanData = any>(
  props: React.PropsWithChildren<SheetProps<TCellData, TCellSpanData>>,
) {
  const {
    cellInfos,
    fixedColWidths = [],
    fixedRowHeights = [],
    minRowHeight,
    columnBorders = [],
    cellSpanInfos,
    cellSpanSpacing,
    firstRowIsHeader,
    fixedColumnIndex,
    borderColor,
    separatorColor,
    onRenderCell,
    onRenderCellSpan,
    onRenderCellSpanCaption,
  } = props;

  // Save copy of row/cell data
  const currentCellInfosRef = useRef<
    Array<Array<{data: TCellData; component: any}>> | undefined
  >();

  // Save copy of cellspan data
  const currentCellSpanInfosRef = useRef<
    | {
        data: CellSpanInfo<TCellSpanData>[] | undefined;
        component: undefined;
      }[]
    | undefined
  >();

  const renderRow = useCallback(
    (row: TCellData[], rowIndex: number) => {
      // Adjust so that we only draw cell span infos that should be displayed.
      const filteredRowCellSpans = cellSpanInfos
        ? getFilteredCellSpanInfos(
            cellSpanInfos[rowIndex],
            fixedColumnIndex,
            row.length,
          )
        : undefined;

      return (
        <Row key={rowIndex} minHeight={minRowHeight}>
          <Layout
            flex
            horizontal
            height={fixedRowHeights[rowIndex]}
            absolute={filteredRowCellSpans ? true : false}>
            {row.map((cell, colIndex) => (
              <CellMemoizer
                key={colIndex}
                colCount={row.length}
                rowCount={cellInfos?.length ?? 0}
                prevCellDataRef={currentCellInfosRef}
                cellData={cell}
                colIndex={colIndex}
                rowIndex={rowIndex}
                borderColor={borderColor}
                separatorColor={separatorColor}
                isHeader={firstRowIsHeader && rowIndex === 0}
                border={columnBorders[colIndex]}
                width={fixedColWidths[colIndex]}
                height={fixedRowHeights[rowIndex]}
                onRenderCell={onRenderCell}
              />
            ))}
          </Layout>
          {filteredRowCellSpans && (
            <CellSpanMemoizer<TCellSpanData>
              colCount={row.length}
              fixedColWidths={fixedColWidths}
              fixedColumnIndex={fixedColumnIndex}
              rowIndex={rowIndex}
              spacing={cellSpanSpacing}
              minHeight={minRowHeight}
              cellSpanInfos={filteredRowCellSpans}
              prevCellSpanDataRef={currentCellSpanInfosRef}
              onRenderCellSpan={assert(
                onRenderCellSpan,
                'To render cell spans you must provide a onRenderCellSpan method',
              )}
              onRenderCellSpanCaption={onRenderCellSpanCaption}
            />
          )}
        </Row>
      );
    },
    [
      borderColor,
      cellInfos?.length,
      cellSpanInfos,
      cellSpanSpacing,
      columnBorders,
      firstRowIsHeader,
      fixedColWidths,
      fixedColumnIndex,
      fixedRowHeights,
      minRowHeight,
      onRenderCell,
      onRenderCellSpan,
      onRenderCellSpanCaption,
      separatorColor,
    ],
  );

  // Render with fixed header
  return firstRowIsHeader && (cellInfos?.length ?? 0) > 0 ? (
    <Layout vertical flex>
      <Layout>{cellInfos && renderRow(cellInfos[0], 0)}</Layout>
      <LayoutScrollable vertical>
        {cellInfos?.map((row, rowIndex) =>
          rowIndex > 0 ? renderRow(row, rowIndex) : null,
        )}
      </LayoutScrollable>
    </Layout>
  ) : (
    <LayoutScrollable flex vertical>
      {cellInfos?.map((row, rowIndex) => renderRow(row, rowIndex))}
    </LayoutScrollable>
  );
};
